/**
 * Computes the position and size of an overlay so it aligns with the video
 * content area rendered by object-fit: contain.
 *
 * This is the single source of truth for overlay-to-video alignment. The
 * annotation viewer (KollabPlayer.tsx syncAnnotationRoots), the annotation
 * editor (annotations/index.ts syncEditorRoot), and the player overlay window
 * (overlay-window/index.ts) all use this function.
 */

export interface ContentBoxPositionInput {
  /** Native video width (pixels from the video file). */
  videoNativeWidth: number;
  /** Native video height (pixels from the video file). */
  videoNativeHeight: number;
  /** Current rendered width of the <video> element (clientWidth). */
  videoElementWidth: number;
  /** Current rendered height of the <video> element (clientHeight). */
  videoElementHeight: number;
  /** Offset of the video element's top edge from the wrapper's top edge (px). */
  videoTopInWrapper: number;
  /** Offset of the video element's left edge from the wrapper's left edge (px). */
  videoLeftInWrapper: number;
}

export interface ContentBoxPosition {
  /** Left offset from wrapper (px, rounded). */
  left: number;
  /** Top offset from wrapper (px, rounded). */
  top: number;
  /** Width of the content box overlay (px, rounded). */
  width: number;
  /** Height of the content box overlay (px, rounded). */
  height: number;
}

/**
 * Given the video's native resolution, the video element's current rendered
 * dimensions, and its offset within the wrapper, compute where an overlay
 * should be positioned so it exactly covers the video content area
 * (the area used by object-fit: contain).
 *
 * Returns null if inputs are invalid (zero dimensions).
 */
export function computeContentBoxPosition(
  input: ContentBoxPositionInput,
): ContentBoxPosition | null {
  const {
    videoNativeWidth,
    videoNativeHeight,
    videoElementWidth,
    videoElementHeight,
    videoTopInWrapper,
    videoLeftInWrapper,
  } = input;

  if (
    videoNativeWidth <= 0 ||
    videoNativeHeight <= 0 ||
    videoElementWidth <= 0 ||
    videoElementHeight <= 0
  ) {
    return null;
  }

  const videoAr = videoNativeWidth / videoNativeHeight;
  const elementAr = videoElementWidth / videoElementHeight;

  let contentW: number;
  let contentH: number;
  let offsetX: number;
  let offsetY: number;

  if (videoAr > elementAr) {
    // Width-constrained: video fills width, letterboxed top/bottom
    contentW = videoElementWidth;
    contentH = videoElementWidth / videoAr;
    offsetX = 0;
    offsetY = (videoElementHeight - contentH) / 2;
  } else {
    // Height-constrained: video fills height, letterboxed left/right
    contentH = videoElementHeight;
    contentW = videoElementHeight * videoAr;
    offsetX = (videoElementWidth - contentW) / 2;
    offsetY = 0;
  }

  return {
    left: Math.round(videoLeftInWrapper + offsetX),
    top: Math.round(videoTopInWrapper + offsetY),
    width: Math.round(contentW),
    height: Math.round(contentH),
  };
}
